import { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Popover } from '../tasks/linear/Popover';
import { useI18n } from '../../contexts/I18nContext';
import {
  addMonths,
  fromDateKey,
  monthGrid,
  monthLabel,
  toDateKey,
  weekdayLabels,
} from '../../lib/calendarDates';

/**
 * Themed replacement for `<input type="date">`.
 *
 * The panel a native date input opens is browser chrome, not DOM: it cannot be
 * reached by CSS beyond `color-scheme`, so it always looked foreign next to the
 * rest of the app. This renders the same `YYYY-MM-DD` contract through the
 * existing `Popover` and the `calendarDates` grid helpers, so the calendar picks
 * up the app's tokens, radius and animation like every other floating panel.
 *
 * Values are plain `YYYY-MM-DD` keys throughout — never `Date.toISOString()` —
 * because the app runs in UTC+2/+3 and a local midnight would serialise back to
 * the previous day.
 */

interface DateFieldProps {
  /** `YYYY-MM-DD`, or '' when unset. */
  value: string;
  onChange: (value: string) => void;
  /** Inclusive bounds as `YYYY-MM-DD`; days outside are not selectable. */
  min?: string;
  max?: string;
  disabled?: boolean;
  /** Shown when `value` is empty. */
  placeholder?: string;
  /** Hides the clear button for fields that must always hold a date. */
  required?: boolean;
  /** Marks the trigger invalid for screen readers when validation failed. */
  'aria-invalid'?: boolean;
  id?: string;
  className?: string;
}

export function DateField({
  value,
  onChange,
  min,
  max,
  disabled,
  placeholder,
  required,
  'aria-invalid': ariaInvalid,
  id,
  className = '',
}: DateFieldProps) {
  const { locale, t } = useI18n();

  const label = value
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(fromDateKey(value))
    : (placeholder ?? t('datePicker.placeholder'));

  return (
    <Popover
      width={272}
      matchTriggerWidth
      trigger={({ open, toggle, ref }) => (
        <button
          id={id}
          ref={ref}
          type="button"
          disabled={disabled}
          onClick={toggle}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          className={`ui-select-trigger ${className}`}
        >
          <span className={value ? 'truncate' : 'truncate text-gray-400'}>{label}</span>
          <span className="flex shrink-0 items-center gap-1">
            {value && !required && !disabled && (
              // A span, not a button: nested buttons are invalid HTML and the
              // outer trigger is already a button.
              <span
                role="button"
                tabIndex={0}
                aria-label={t('datePicker.clear')}
                className="rounded p-0.5 text-gray-400 hover:bg-accent hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange('');
                  }
                }}
              >
                <X size={13} />
              </span>
            )}
            <Calendar size={14} className="text-gray-400" />
          </span>
        </button>
      )}
    >
      {(close) => (
        <CalendarPanel
          value={value}
          min={min}
          max={max}
          onPick={(key) => {
            onChange(key);
            close();
          }}
        />
      )}
    </Popover>
  );
}

/**
 * The month grid on its own, for callers that already own a floating panel.
 *
 * `Popover` renders into a portal at `document.body`, so a nested `DateField`
 * would look like an outside click to the outer popover and collapse it. Those
 * callers embed this directly instead.
 */
export function CalendarPanel({
  value,
  min,
  max,
  onPick,
}: {
  value: string;
  min?: string;
  max?: string;
  onPick: (key: string) => void;
}) {
  const { locale, t } = useI18n();
  const today = toDateKey(new Date());
  // Open on the selected month, or on today's when the field is empty.
  const [cursor, setCursor] = useState(() => fromDateKey(value || today));

  const weeks = monthGrid(cursor.getFullYear(), cursor.getMonth());
  // `YYYY-MM-DD` sorts lexicographically, so plain string compares are enough.
  const outOfRange = (key: string) => (min && key < min) || (max && key > max);

  return (
    <div className="p-2">
      <div className="flex items-center justify-between px-1 pb-2">
        <button
          type="button"
          aria-label={t('datePicker.previousMonth')}
          onClick={() => setCursor(addMonths(cursor, -1))}
          className="rounded p-1 text-gray-500 hover:bg-accent hover:text-foreground"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="text-sm font-semibold capitalize text-foreground">
          {monthLabel(locale, cursor.getFullYear(), cursor.getMonth())}
        </span>
        <button
          type="button"
          aria-label={t('datePicker.nextMonth')}
          onClick={() => setCursor(addMonths(cursor, 1))}
          className="rounded p-1 text-gray-500 hover:bg-accent hover:text-foreground"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {weekdayLabels(locale).map((w) => (
          <span
            key={w}
            className="py-1 text-center text-[11px] font-medium uppercase text-gray-400"
          >
            {w.slice(0, 2)}
          </span>
        ))}

        {weeks.flat().map((day) => {
          const selected = day.key === value;
          const blocked = outOfRange(day.key);
          return (
            <button
              key={day.key}
              type="button"
              disabled={!!blocked}
              onClick={() => onPick(day.key)}
              aria-current={day.key === today ? 'date' : undefined}
              className={[
                'h-8 rounded text-sm transition-colors',
                selected
                  ? 'bg-primary font-semibold text-primary-foreground'
                  : blocked
                    ? 'cursor-not-allowed text-gray-300'
                    : day.inMonth
                      ? 'text-foreground hover:bg-accent'
                      : 'text-gray-400 hover:bg-accent',
                !selected && day.key === today ? 'ring-1 ring-inset ring-ring' : '',
              ].join(' ')}
            >
              {day.date.getDate()}
            </button>
          );
        })}
      </div>

      <div className="mt-2 border-t border-border pt-2">
        <button
          type="button"
          disabled={!!outOfRange(today)}
          onClick={() => onPick(today)}
          className="w-full rounded px-2 py-1.5 text-sm font-medium text-foreground hover:bg-accent disabled:cursor-not-allowed disabled:text-gray-400"
        >
          {t('datePicker.today')}
        </button>
      </div>
    </div>
  );
}
